import type { CSSProperties } from "react";
import AlertModal from "@components/modal/AlertModal";
import TranslatableText from "@components/translations/TranslatableText";
import { AsyncJobStatus } from "@app/features/asyncJobs/asyncJobsTypes";

interface ProjectCopyBlockModalProps {
	isOpen: boolean;
	progress: number;
	status: AsyncJobStatus | null;
	errorMessage?: string | null;
	onReturnToList: () => void;
}

const progressBarStyle: CSSProperties = {
	width: '100%',
	height: '8px',
	background: 'var(--light-gray)',
	borderRadius: '4px',
	overflow: 'hidden',
};

const progressFillStyle: CSSProperties = {
	height: '100%',
	background: 'var(--primary-bright)',
	transition: 'width 0.3s ease',
};

const ProjectCopyBlockModal = ({
	isOpen,
	progress,
	status,
	errorMessage,
	onReturnToList
}: ProjectCopyBlockModalProps) => {
	const isFailed = status === 'failed';
	const isPending = status === 'pending' || status === 'running';

	const returnButton = {
		onClick: onReturnToList,
		children: <TranslatableText translationKey="project.return_to_project_list" />,
		type: "button" as const,
	};

	return (
		<AlertModal
			modalOpen={isOpen}
			handleCancel={onReturnToList}
			headerText="project.copy_in_progress"
			buttons={[returnButton]}
			size="md"
		>
			<div className="w-100 h-100 p-3 d-flex f-column align-items-center justify-content-center gap-8">
				{isFailed ? (
					<>
						<span className="fw-600 fs-14 text-center text-danger">
							<TranslatableText translationKey="project.copy_in_progress_error" />
						</span>
						{errorMessage && (
							<span className="fs-12 text-muted text-center">{errorMessage}</span>
						)}
					</>
				) : (
					<>
						<span className="fw-600 fs-14 text-center w-100">
							<TranslatableText translationKey="project.copy_in_progress_message" />
						</span>
						{isPending && (
							<div className="w-100 d-flex f-column align-items-center gap-8">
								<div
									style={progressBarStyle}
									role="progressbar"
									aria-valuenow={progress}
									aria-valuemin={0}
									aria-valuemax={100}
								>
									<div style={{ ...progressFillStyle, width: `${progress}%` }} />
								</div>
								<span className="fs-12 text-muted">{progress}%</span>
							</div>
						)}
					</>
				)}
			</div>
		</AlertModal>
	);
};

export default ProjectCopyBlockModal;
